import { useEffect, useState } from "react";
import { UserPlus, ShieldCheck, ShieldOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";

const ALL_ROLES = ["super_admin", "admin", "manager", "berater", "trainee", "compliance", "backoffice"];

export default function AdminUsers() {
  const { t, tt } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: "berater", must_change_password: true, require_2fa_setup: false });

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async () => {
    if (!form.email || !form.password || !form.first_name || !form.last_name) {
      toast.error(t("Bitte alle Felder ausf\u00fcllen", "Please fill in all fields"));
      return;
    }
    try {
      const res = await api.post("/admin/users", form);
      setUsers((us) => [...us, res.data]);
      setCreateOpen(false);
      setForm({ email: "", password: "", first_name: "", last_name: "", role: "berater", must_change_password: true, require_2fa_setup: false });
      toast.success(t("Benutzer angelegt", "User created"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Fehler beim Anlegen", "Error creating user"));
    }
  };

  const update = async (id, changes) => {
    try {
      const res = await api.patch(`/admin/users/${id}`, changes);
      setUsers((us) => us.map((u) => (u.id === id ? res.data : u)));
      toast.success(t("Benutzer aktualisiert", "User updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Fehler", "Error"));
    }
  };

  const reset2FA = async (u) => {
    if (!window.confirm(t(`2FA f\u00fcr ${u.first_name} ${u.last_name} wirklich zur\u00fccksetzen? Alle Sitzungen werden beendet.`, `Really reset 2FA for ${u.first_name} ${u.last_name}? All sessions will be terminated.`))) return;
    try {
      await api.post(`/admin/users/${u.id}/reset-2fa`);
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, totp_enabled: false } : x)));
      toast.success(t("2FA zur\u00fcckgesetzt", "2FA reset"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Fehler", "Error"));
    }
  };

  const toggleRequire2FA = async (u) => {
    const newVal = !u.require_2fa_setup;
    if (newVal && !window.confirm(t(
      `2FA f\u00fcr ${u.first_name} ${u.last_name} verbindlich vorschreiben? Bestehende Sitzungen werden beendet und die Einrichtung ist beim n\u00e4chsten Login Pflicht.`,
      `Require 2FA for ${u.first_name} ${u.last_name}? Existing sessions will be terminated and setup is mandatory at next login.`
    ))) return;
    try {
      await api.post(`/admin/users/${u.id}/require-2fa`, { required: newVal });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, require_2fa_setup: newVal } : x)));
      toast.success(newVal ? t("2FA-Pflicht gesetzt \u2013 Einrichtung beim n\u00e4chsten Login", "2FA required \u2013 setup at next login") : t("2FA-Pflicht aufgehoben", "2FA requirement lifted"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Fehler", "Error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Benutzer & Rollen", "Users & Roles")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Mitarbeiterverwaltung mit rollenbasierten Zugriffen", "Employee management with role-based access")}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-[10px] bg-[#0B1220]" data-testid="user-create-button">
              <UserPlus className="h-4 w-4 mr-1.5" /> {t("Neuer Benutzer", "New user")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>{t("Neuen Benutzer anlegen", "Create new user")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Vorname", "First name")}</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-[10px]" data-testid="user-first-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Nachname", "Last name")}</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-[10px]" data-testid="user-last-name-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>E-Mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-[10px]" data-testid="user-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Initial-Passwort (min. 10 Zeichen, Gro\u00df-/Kleinbuchstaben, Zahl)", "Initial password (min. 10 characters, upper/lower case, number)")}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-[10px]" data-testid="user-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Rolle", "Role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-[10px]" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ALL_ROLES.filter((r) => r !== "super_admin" || me?.role === "super_admin").map((r) => (
                      <SelectItem key={r} value={r}>{tt(ROLE_LABELS[r])}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-[12px] border border-border bg-muted/40 p-3 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("Sicherheit", "Security")}</p>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.must_change_password}
                    onCheckedChange={(v) => setForm({ ...form, must_change_password: !!v })}
                    className="mt-0.5"
                    data-testid="user-must-change-pw-checkbox"
                  />
                  <span>
                    {t("Passwort\u00e4nderung beim ersten Login verlangen", "Require password change at first login")}
                    <span className="block text-xs text-muted-foreground">{t("Empfohlen \u2013 das Initial-Passwort ist Ihnen bekannt.", "Recommended \u2013 you know the initial password.")}</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={form.require_2fa_setup}
                    onCheckedChange={(v) => setForm({ ...form, require_2fa_setup: !!v })}
                    className="mt-0.5"
                    data-testid="user-require-2fa-checkbox"
                  />
                  <span>
                    {t("2FA-Einrichtung verpflichtend", "Require 2FA setup")}
                    <span className="block text-xs text-muted-foreground">{t("Mitarbeiter wird beim ersten Login durch die Einrichtung gef\u00fchrt.", "Employee is guided through setup at first login.")}</span>
                  </span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-[10px] bg-[#0B1220]" data-testid="user-create-submit">{t("Anlegen", "Create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-[16px] overflow-hidden">
        {loading ? (
          <CardContent className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Name", "Name")}</TableHead>
                  <TableHead>{t("Rolle", "Role")}</TableHead>
                  <TableHead className="hidden md:table-cell">Onboarding</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>{t("Aktiv", "Active")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => update(u.id, { role: v })} disabled={u.id === me?.id || (u.role === "super_admin" && me?.role !== "super_admin")}>
                        <SelectTrigger className="h-8 w-[150px] rounded-[8px] text-xs" data-testid={`user-role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ALL_ROLES.filter((r) => r !== "super_admin" || me?.role === "super_admin").map((r) => (
                            <SelectItem key={r} value={r}>{tt(ROLE_LABELS[r])}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant={u.onboarding_status === "completed" ? "secondary" : "outline"} className="rounded-full text-[10px]">
                        {u.onboarding_status === "completed" ? t("Abgeschlossen", "Completed") : "Onboarding"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {u.totp_enabled ? (
                          <>
                            <Badge variant="secondary" className="rounded-full text-[10px] whitespace-nowrap shrink-0" data-testid={`user-2fa-badge-${u.id}`}>
                              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {t("Aktiv", "Active")}</span>
                            </Badge>
                            {me?.role === "super_admin" && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs rounded-[8px]" onClick={() => reset2FA(u)} data-testid={`user-2fa-reset-${u.id}`}>
                                {t("Reset", "Reset")}
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge variant="outline" className="rounded-full text-[10px] whitespace-nowrap shrink-0" data-testid={`user-2fa-badge-${u.id}`}>
                              <span className="inline-flex items-center gap-1 text-muted-foreground">
                                <ShieldOff className="h-3 w-3" /> {u.require_2fa_setup ? t("Angefordert", "Required") : t("Aus", "Off")}
                              </span>
                            </Badge>
                            {me?.role === "super_admin" && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs rounded-[8px] whitespace-nowrap" onClick={() => toggleRequire2FA(u)} data-testid={`user-2fa-require-${u.id}`}>
                                {u.require_2fa_setup ? t("Aufheben", "Lift") : t("Anfordern", "Require")}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={u.status === "active"}
                        disabled={u.id === me?.id}
                        onCheckedChange={(v) => update(u.id, { status: v ? "active" : "inactive" })}
                        data-testid={`user-status-switch-${u.id}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
